// Admin Report Detail Page - View and resolve individual reports
import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../App";
import { ArrowLeft, CheckCircle, XCircle, Flag, User, MessageSquare, ExternalLink } from "lucide-react";

const API = window.location.origin;

function StatusPill({ status }) {
  const s = (status || "open").toLowerCase();
  if (s === "open") return (
    <span className="text-xs px-3 py-1 rounded-full font-medium bg-rose-500/10 text-rose-400 border border-rose-500/30">
      🔴 Open
    </span>
  );
  if (s === "resolved") return (
    <span className="text-xs px-3 py-1 rounded-full font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
      ✅ Resolved
    </span>
  );
  return (
    <span className="text-xs px-3 py-1 rounded-full font-medium bg-slate-500/10 text-slate-400 border border-slate-500/30">
      ⏭️ Ignored
    </span>
  );
}

export default function AdminReportDetailPage() {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const { token, user } = useContext(AuthContext);

  const [report, setReport] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const isAdmin = user?.is_admin;

  async function load() {
    if (!isAdmin || !reportId) return;
    setBusy(true);
    setToast(null);
    try {
      const res = await fetch(`${API}/api/admin/reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Failed to load report");
      setReport(data);
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    load();
    
  }, [reportId, isAdmin]);

  async function resolve(action) {
    setBusy(true);
    setToast(null);
    try {
      const res = await fetch(`${API}/api/admin/reports/${reportId}/resolve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action, note }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Failed to update report");
      setToast({ type: "success", msg: `Report ${action === "resolve" ? "resolved" : "ignored"} ✅` });
      setReport(data);
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    } finally {
      setBusy(false);
    }
  }

  // Check admin access after hooks
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-slate-400">Admin access required</div>
      </div>
    );
  }

  if (!report && !busy) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <button
            onClick={() => navigate("/admin/reports")}
            className="inline-flex items-center space-x-2 text-sm text-slate-400 hover:text-slate-200 transition"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Reports</span>
          </button>
          <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-900/50 p-8 text-center">
            <div className="text-slate-500">Report not found</div>
          </div>
        </div>
      </div>
    );
  }

  const reportedId = report?.reported_user_id;
  const isOpen = report?.status === "open";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/admin/reports")}
            className="inline-flex items-center space-x-2 text-sm text-slate-400 hover:text-slate-200 transition"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Reports</span>
          </button>
          <div className="text-xs text-slate-500 font-mono">ID: {reportId}</div>
        </div>

        {/* Main Card */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 overflow-hidden">
          {/* Title Bar */}
          <div className="p-4 border-b border-slate-800 bg-slate-800/30">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-rose-500/10">
                  <Flag className="w-5 h-5 text-rose-400" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-slate-200">
                    {report?.reason || report?.type || "User Report"}
                  </h1>
                  <div className="text-xs text-slate-500">
                    {report?.created_at ? new Date(report.created_at).toLocaleString() : ""}
                  </div>
                </div>
              </div>
              <StatusPill status={report?.status} />
            </div>
          </div>

          {/* Toast */}
          {toast && (
            <div
              className={`mx-4 mt-4 rounded-xl border px-4 py-3 text-sm flex items-center gap-2 ${
                toast.type === "error"
                  ? "border-rose-500/30 bg-rose-500/10 text-rose-400"
                  : "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
              }`}
            >
              {toast.type === "error" ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
              {toast.msg}
            </div>
          )}

          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Reporter & Reported */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                  <User className="w-3 h-3" />
                  Reporter
                </div>
                <div className="text-sm font-medium text-slate-200">
                  {report?.reporter_email || report?.reporter_user_id || "Unknown"}
                </div>
              </div>
              <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                  <User className="w-3 h-3" />
                  Reported User
                </div>
                <div className="text-sm font-medium text-slate-200">
                  {report?.reported_email || report?.reported_user_id || "Unknown"}
                </div>
              </div>
            </div>

            {/* Report Message */}
            {report?.message && (
              <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                  <MessageSquare className="w-3 h-3" />
                  Report Message
                </div>
                <div className="text-sm text-slate-300 whitespace-pre-wrap">
                  {report.message}
                </div>
              </div>
            )}

            {/* Resolution Note (if resolved) */}
            {report?.resolution_note && (
              <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                <div className="text-xs text-emerald-400 mb-2">Resolution Note</div>
                <div className="text-sm text-emerald-300">{report.resolution_note}</div>
                {report.resolved_at && (
                  <div className="text-xs text-emerald-500 mt-2">
                    Resolved: {new Date(report.resolved_at).toLocaleString()}
                  </div>
                )}
              </div>
            )}

            {/* Action Section (only if open) */}
            {isOpen && (
              <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
                <div className="text-xs text-slate-500 mb-3">Resolution Note (optional)</div>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="w-full rounded-xl border border-slate-700 bg-slate-900 p-3 text-sm text-slate-200 placeholder:text-slate-500"
                  rows={3}
                  placeholder="Add a note for the audit trail…"
                />
                <div className="flex flex-wrap gap-3 mt-4">
                  <button
                    onClick={() => resolve("resolve")}
                    disabled={busy}
                    className="inline-flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm font-medium text-emerald-400 hover:bg-emerald-500/20 disabled:opacity-50 transition"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Resolve
                  </button>
                  <button
                    onClick={() => resolve("ignore")}
                    disabled={busy}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-700 disabled:opacity-50 transition"
                  >
                    <XCircle className="w-4 h-4" />
                    Ignore
                  </button>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-3">
              {reportedId && (
                <>
                  <button
                    onClick={() => navigate(`/admin/users/${reportedId}`)}
                    className="inline-flex items-center gap-2 rounded-xl border border-violet-500/30 bg-violet-500/10 px-4 py-2 text-sm font-medium text-violet-400 hover:bg-violet-500/20 transition"
                  >
                    <User className="w-4 h-4" />
                    View User 360
                  </button>
                  <button
                    onClick={() => navigate(`/admin/moderation?q=${encodeURIComponent(report?.reported_email || reportedId)}`)}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-700 transition"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open in Moderation
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
